import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { getLimit } from "@/data/regulatoryStandards";
import type { QualityTier } from "@/types";

const EXAMPLES = ["Lead", "Arsenic", "Benzo(a)pyrene"];
const TIERS: QualityTier[] = ["unrestricted", "residential", "commercial"];

function limit(name: string, tier: QualityTier, state: "NY" | "NJ") {
  const l = getLimit(name, "clean-fill", tier, state).limit;
  return l == null ? "—" : `${l}`;
}

export default function CleanFillGuide() {
  useEffect(() => {
    document.title = "Clean Fill vs. Regular Dirt: NY & NJ Guide | Dirt Merchant Marketplace";
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1 container max-w-3xl py-10 space-y-8">
        <header className="space-y-3">
          <p className="text-sm uppercase tracking-wide text-accent">Guide</p>
          <h1 className="text-4xl font-bold">Clean fill vs. regular dirt</h1>
          <p className="text-lg text-muted-foreground">
            What separates clean fill from ordinary dirt and structural fill, and how New York and New Jersey soil standards decide where material can go.
          </p>
        </header>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">The short answer</h2>
          <p>
            "Regular dirt" is any excavated soil whose chemistry is unknown. "Clean fill" is soil that lab testing shows meets the strictest use standard, so it can be placed almost anywhere. The difference is not how the soil looks but what the lab report says.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">Three kinds of fill</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Clean fill</strong> — tested soil that falls within unrestricted-use limits. Suitable for residential yards, parks and most receiving sites.</li>
            <li><strong>Regular (general) fill</strong> — soil, often from excavation, that has not been tested or only meets residential, commercial or industrial limits. Where it can go depends on its results.</li>
            <li><strong>Structural fill</strong> — material chosen for engineering properties such as compaction and drainage (often sand, gravel or crushed stone). It still needs environmental testing if it is soil from another site.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">How NY and NJ standards work</h2>
          <p>
            New York compares soil to the Soil Cleanup Objectives in 6 NYCRR Part 375, which set limits by use: unrestricted, residential, restricted-residential, commercial and industrial. New Jersey uses NJDEP's soil remediation standards, including the residential direct contact standards (RDCSRS). A sample is only clean for a use if every tested parameter falls within that use's limit.
          </p>
          <p>Examples of limits used by this marketplace's checker (mg/kg):</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr><th className="py-1 pr-3">Parameter</th>{TIERS.map((t) => <th key={t} className="pr-3 capitalize">NY {t}</th>)}<th>NJ residential</th></tr>
              </thead>
              <tbody>
                {EXAMPLES.map((p) => (
                  <tr key={p} className="border-t">
                    <td className="py-1 pr-3">{p}</td>
                    {TIERS.map((t) => <td key={t} className="pr-3">{limit(p, t, "NY")}</td>)}
                    <td>{limit(p, "residential", "NJ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-muted-foreground">
            Always confirm against the current published regulation and your consultant's judgment before moving material.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">How to prove soil is clean fill</h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li>Sample the soil and send it to a certified (NY ELAP) laboratory.</li>
            <li>Compare every result against the limits for the intended use.</li>
            <li>Keep the lab report with the material through hauling, so the receiving site can check it.</li>
          </ol>
        </section>

        <section className="rounded-lg border p-6 space-y-3">
          <h2 className="text-xl font-semibold">Check your soil or find material</h2>
          <p className="text-muted-foreground">Upload lab results to compare them against NY and NJ standards, or browse tested material near your site.</p>
          <div className="flex flex-wrap gap-2">
            <Button asChild><Link to="/analysis">Check lab results</Link></Button>
            <Button asChild variant="outline"><Link to="/marketplace">Browse materials</Link></Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
